import React from 'react';
import { type Analytics } from './email_analytics_card';
import { type Metric } from './website_traffic';
import { NonBasicCardStyling } from './util';
import { type PreviewEyeProps } from './preview_eye';

type Props = {
  casSigned: number;
  casViewed: number;
  documentDownloads: number;
  emailAnalytics: Analytics[];
  preview: boolean;
  previewEye: PreviewEyeProps;
  totalPublicDocViews: number;
  websiteViews: Metric;
  uniqueLockedViews: number;
};

type FunnelSegmentProps = {
  index: number;
  item: {
    name: string;
    value: number;
    fill: string;
  };
};

const EMAILS_SENT_COLOR = '#203D88';
const VIEWED_EMAIL_COLOR = '#2042A7';
const VIEWED_WEBSITE_COLOR = '#2153D4';
const VIEWED_PUBLIC_DOCS_COLOR = '#2968E7';
const VIEWED_CA_COLOR = '#3F86F2';
const SIGNED_CA_COLOR = '#63A8F7';
const DOCUMENT_VIEWS_COLOR = '#95C7FB';
const DOWNLOADS_COLOR = '#BBD7F9';

const FunnelSegment = ({ index, item }: FunnelSegmentProps) => (
  <div className="funnel-segment-wrapper">
    <svg
      fill="none"
      height="36"
      preserveAspectRatio="none"
      viewBox={`0 0 377 42`}
      width={`${100 - index * 6}%`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={`
                    M0.291487 7.70946
                    C-0.851808 3.86297 2.03 0 6.04281 0
                    H370.052
                    C374.037 0 376.914 3.81185 375.823 7.64369
                    L367.301 37.5607
                    C366.567 40.1388 364.211 41.917 361.531 41.917
                    H14.9351
                    C12.2798 41.917 9.94026 40.1717 9.18374 37.6265
                    L0.291487 7.70946
                    Z
                `}
        fill={item.fill}
      />
    </svg>
    <div className="funnel-text">
      {item.value} {item.name}
    </div>
  </div>
);

export default function ActivityFunnel({
  casSigned,
  casViewed,
  documentDownloads,
  emailAnalytics,
  preview,
  previewEye,
  totalPublicDocViews,
  websiteViews,
  uniqueLockedViews
}: Props) {
  const totalEmailsSent = emailAnalytics.reduce((sum, email) => sum + email.numDelivered, 0);
  const totalOpens = emailAnalytics.reduce((sum, email) => sum + email.numUniqueOpens, 0);

  const funnelData = [
    { name: 'Emails Sent', value: totalEmailsSent, fill: EMAILS_SENT_COLOR },
    { name: 'Opened Email', value: totalOpens, fill: VIEWED_EMAIL_COLOR },
    { name: 'Visited Website', value: websiteViews.aggregateValue, fill: VIEWED_WEBSITE_COLOR },
    { name: 'Viewed Public Docs', value: totalPublicDocViews, fill: VIEWED_PUBLIC_DOCS_COLOR },
    { name: 'Viewed CA', value: casViewed, fill: VIEWED_CA_COLOR },
    { name: 'Signed CA', value: casSigned, fill: SIGNED_CA_COLOR },
    { name: 'Viewed Locked', value: uniqueLockedViews, fill: DOCUMENT_VIEWS_COLOR },
    { name: 'Downloads', value: documentDownloads, fill: DOWNLOADS_COLOR },
  ];

  return (
    <div className={NonBasicCardStyling}>
      <div className="d-flex justify-content-between align-items-center">
        <h4 className="m-0 fw-normal">Activity Funnel</h4>
        { !preview && previewEye }
      </div>
      <div className="funnel-container">
        {funnelData.map((item, index) => (
          <FunnelSegment index={index} item={item} key={index} />
        ))}
      </div>
    </div>
  );
}
